import React, { useEffect } from "react";
import Loader from "../components/Loader";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import Newsletter from "../components/Newsletter";
import ContactForm from "../components/ContactForm";
import Breadcrumbs from "../components/Breadcrumbs";
import { useLocation } from "react-router-dom";
import StructuredData from "../seo/StructuredData";
import { breadcrumbJsonLd } from "../seo/jsonld";
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL } from "../seo/site";

export default function Contact({ setHeader }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const pageLang = i18n?.language === "me" ? "me" : "en";
  const pageUrl = `${SITE_URL}${location.pathname}`;
  const pageTitle = `${t("header.contact.pageTitle")} - ${SITE_NAME}`;
  const pageDescription = t("header.contact.metaDescription");
  const enUrl = `${SITE_URL}/en/contact`;
  const meUrl = `${SITE_URL}/me/kontakt`;
  const breadcrumbItems = [
    { name: SITE_NAME, item: `${SITE_URL}/${pageLang}` },
    { name: t("header.contact.pageTitle"), item: pageUrl },
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 1) {
        setHeader(true);
      } else {
        setHeader(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [setHeader]);

  return (
    <>
      <Helmet htmlAttributes={{ lang: pageLang }}>
        {/* <!-- meta tag --> */}
        <meta charset="utf-8" />
        <title>
          {pageTitle}
        </title>

        {/* <!-- Meta Tags--> */}
        <meta
          name="description"
          content={pageDescription}
        />
        <meta
          name="keywords"
          content="durō, duro, niche perfumery, niche parfimerija,  parfimerija, galerija"
        />
        <meta name="author" content="intellect.se" />
        <link rel="canonical" href={pageUrl} />
        <link rel="alternate" href={enUrl} hreflang="en" />
        <link rel="alternate" href={meUrl} hreflang="me" />
        <link rel="alternate" href={enUrl} hreflang="x-default" />

        {/* <!-- responsive tag --> */}
        <meta http-equiv="x-ua-compatible" content="ie=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* <!-- Facebook Metadata --> */}
        <meta property="og:type" content="website" />
        <meta property="og:image:height" content="1200" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:image" content={DEFAULT_OG_IMAGE} />
      </Helmet>
      <Loader />
      {/* <!-- Main content Start --> */}
      <div className="main-content">
        {/* <!-- Breadcrumbs Start --> */}
        <div className="rs-breadcrumbs img14">
          <div className="container">
            <div className="breadcrumbs-inner">
              <h1 className="page-title">
                {t("home.contact.contact")}
                {/*<span className="watermark">{home.contact.contact}</span>*/}
              </h1>
            </div>
          </div>
        </div>
        <Breadcrumbs bg="lion-bg" />
        {/* <!-- Breadcrumbs End --> */}

                {/* <!-- Locations Section Start --> */}
                <div
          id="map"
          className="rs-contact contact-page lion-bg home-style1 home-style3 contact-home-style4 height-ctrl pt-90 md-pb-50 md-pt-40"
        >
          <div className="container">
            <div className="sec-title text-center mb-60 md-mb-40">
              <h2 className="title title2 title3 text-middle">
                {t("home.contact.mapTitle")}
              </h2>
            </div>
            {/* <!-- Contact Section Start --> */}
            <div className="rs-contact">
              <div className="row y-middle contact-locations">
                <div className="col-lg-4 pr-40 md-pr-15 md-mb-50 contact-location">
                  <img
                      src="/assets/images/contact/podgorica-001.jpg"
                      alt="DURŌ Podgorica"
                      title="DURŌ Podgorica"
                      className="mb-40 contact-location-photo"
                  />
                  <div className="row y-middle">
                  <div className="widget-item mb-20">
                    <div className="address-item">
                      <div className="address-icon">
                        <img
                          src="/assets/images/contact/icons/1.png"
                          alt="Images"
                        />
                      </div>
                      <div className="address-text">
                        <h3 className="title">Podgorica</h3>
                        <p className="services-txt">
                          Bulevar Džordža Vašingtona 68, 81000 Podgorica                      
                          <br />
                          {t("footer.footerMain.workHours")}: 10.00 - 21.00
                          <br />
                          {t("footer.footerMain.sunday")}
                          <br /><br />
                          Tel.:<a href="tel:+382 68 002 002">+382 68 002 002</a>
                        </p>
                      </div>
                    </div>
                  </div>
                  </div>
                  <div className="contact-map mt-30">
                    <iframe
                      title="DURŌ Podgorica"
                      src="https://www.google.com/maps?q=DUR%C5%8C%20Podgorica%2C%20Bulevar%20D%C5%BEord%C5%BEa%20Va%C5%A1ingtona%2068%2C%20Podgorica&output=embed"
                    ></iframe>
                    <a
                      className="map-link d-lg-none"
                      href="https://www.google.com/maps/search/?api=1&query=DUR%C5%8C%20Podgorica%2C%20Bulevar%20D%C5%BEord%C5%BEa%20Va%C5%A1ingtona%2068%2C%20Podgorica"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open in Maps
                    </a>
                  </div>
                </div>
                <div className="col-lg-4 pr-40 md-pr-15 md-mb-50 contact-location">
                  <img
                      src="/assets/images/contact/capital-plaza-001.jpg"
                      alt="DURŌ Capital Plaza"
                      title="DURŌ Capital Plaza"
                      className="mb-40 contact-location-photo"
                  />
                  <div className="row y-middle">
                  <div className="widget-item mb-20">
                      <div className="address-item">
                          <div className="address-icon">
                            <img
                              src="/assets/images/contact/icons/1.png"
                              alt="Images"
                            />
                          </div>
                          <div className="address-text">
                            <h3 className="title">Capital Plaza</h3>
                            <p className="services-txt">
                              Cetinjska 9, 81000 Podgorica
                              <br />
                              {t("footer.footerMain.workHours")}: 10.00 - 21.00
                              <br />
                              {t("footer.footerMain.sunday")}
                              <br /><br />
                              Tel.:<a href="tel:+382 68 002 004">+382 68 002 004</a>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="contact-map mt-30">
                      <iframe
                        title="DURŌ Capital Plaza"
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1478988.5362572407!2d18.525643151586753!3d43.613839302461365!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x134deb8d4dded0bb%3A0x5e7579a43be61d25!2sDUR%C5%8C%20Capital%20Plaza%20%7C%20Niche%20perfumery!5e0!3m2!1sen!2srs!4v1768754006406!5m2!1sen!2srs"
                      ></iframe>
                      <a
                        className="map-link d-lg-none"
                        href="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1478988.5362572407!2d18.525643151586753!3d43.613839302461365!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x134deb8d4dded0bb%3A0x5e7579a43be61d25!2sDUR%C5%8C%20Capital%20Plaza%20%7C%20Niche%20perfumery!5e0!3m2!1sen!2srs!4v1768754006406!5m2!1sen!2srs"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open in Maps
                      </a>
                    </div>
                </div>
                <div className="col-lg-4 pr-40 md-pr-15 md-mb-50 contact-location">
                  <img
                      src="/assets/images/contact/porto-montenegro-001.jpg"
                      alt="DURŌ Porto Montenegro"
                      title="DURŌ Porto Montenegro"
                      className="mb-40 contact-location-photo"
                  />
                  <div className="row y-middle">
                  <div className="widget-item mb-20">
                      <div className="address-item">
                          <div className="address-icon">
                            <img
                              src="/assets/images/contact/icons/1.png"
                              alt="Images"
                            />
                          </div>
                          <div className="address-text">
                            <h3 className="title">Porto Montenegro</h3>
                            <p className="services-txt">
                              Strada Navale, Porto Montenegro, Tivat
                              <br />
                              Privremeno zatvorena zbog renoviranja Strada Navale
                              <br /><br />
                              Tel.:<a href="tel:+382 68 09 54 31">+382 68 09 54 31</a>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="contact-map mt-30">
                      <iframe
                        title="DURŌ Porto Montenegro"
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2944.6417489358846!2d18.693786499999998!3d42.435359899999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x134c319d3b46d9b7%3A0x2923ebbc972bd499!2sDUR%C5%8C%20Porto%20Montenegro%20%7C%20Niche%20perfumery!5e0!3m2!1sen!2srs!4v1768650728724!5m2!1sen!2srs"
                      ></iframe>
                      <a
                        className="map-link d-lg-none"
                        href="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2944.6417489358846!2d18.693786499999998!3d42.435359899999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x134c319d3b46d9b7%3A0x2923ebbc972bd499!2sDUR%C5%8C%20Porto%20Montenegro%20%7C%20Niche%20perfumery!5e0!3m2!1sen!2srs!4v1768650728724!5m2!1sen!2srs"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open in Maps
                      </a>
                    </div>
                </div>
              </div>
            </div>
            {/* <!-- Contact Section End --> */}
          </div>
        </div>
        {/* <!-- Locations Section End -->  */}

        {/* <!-- Contact Section Start --> */}
        <div className="rs-contact contact-page lion-bg home-style1 home-style3 contact-home-style4 height-ctrl pt-90 md-pb-50 md-pt-40">
          <div className="container">
            <div className="sec-title text-center mb-60 md-mb-40">
              <h2 className="title title2 title3 text-middle">
                {t("home.contact.title")}
              </h2>
            </div>
            {/* <!-- Contact Section Start --> */}
            <div className="rs-contact">
              <div className="row y-middle justify-content-center">
                <div className="col-lg-6 col-md-10">
                  <div className="contact-wrap">
                    <div id="form-messages"></div>
                    <ContactForm />
                  </div>
                </div>
              </div>
            </div>
            {/* <!-- Contact Section End --> */}
          </div>
        </div>
        {/* <!-- Contact Section End -->  */}

        {/* <!-- Newsletter section start --> */}
        <div className="rs-newsletter lion-bg style1 pt-90 md-pt-60 mb--65">
          <Newsletter />
        </div>
        {/* <!-- Newsletter section end --> */}
      </div>
      {/* <!-- Main content End --> */}
      <StructuredData data={breadcrumbJsonLd(breadcrumbItems)} />
    </>
  );
}
